/**
 * Azure Application Insights client-side plugin.
 * Initializes reliability telemetry (page views, route changes, fetch/ajax) when
 * NUXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING is configured. No-ops safely otherwise.
 */

import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import { onCLS, onINP, onLCP } from 'web-vitals';

type WebVitalMetric = {
  name: 'CLS' | 'INP' | 'LCP';
  value: number;
  rating: string;
  id: string;
};

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const connectionString = typeof config.public.appInsightsConnectionString === 'string'
    ? config.public.appInsightsConnectionString
    : '';

  if (!connectionString) return;

  // Guard against duplicate initialization during HMR.
  if (nuxtApp._appInsightsInitialized) return;
  nuxtApp._appInsightsInitialized = true;

  let appInsights: ApplicationInsights | null = null;
  const pendingMetrics: WebVitalMetric[] = [];

  const trackWebVital = (metric: WebVitalMetric) => {
    if (!appInsights) {
      pendingMetrics.push(metric);
      return;
    }

    appInsights.trackEvent({
      name: 'QikSolve/WebVital',
      properties: {
        metric: metric.name,
        rating: metric.rating,
        metricId: metric.id,
        path: window.location.pathname,
      },
      measurements: {
        value: metric.value,
      },
    });
  };

  onCLS(trackWebVital);
  onINP(trackWebVital);
  onLCP(trackWebVital);

  const initializeTelemetry = async () => {
    const { ApplicationInsights: ApplicationInsightsClient } = await import('@microsoft/applicationinsights-web');

    appInsights = new ApplicationInsightsClient({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        disableFetchTracking: false,
        disablePageUnloadEvents: ['unload'],
      },
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    for (const metric of pendingMetrics.splice(0)) {
      trackWebVital(metric);
    }
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initializeTelemetry, { timeout: 2000 });
  } else {
    globalThis.setTimeout(initializeTelemetry, 1500);
  }
});
